import { COLORS } from './constants';

import { HOAEnvironmentalRiskCategory, HOAEnvironmentalRiskFinding } from '@/types/HOA';

// ─── Environmental & Covenant Risk taxonomy ───────────────────────────────
// Built and pressure-tested against the real 17-finding extraction for 283
// Wild Rose Way (Contempo & Traditions HOA) — a genuine VOC-contamination
// covenant + active vapor mitigation system case, not a synthetic example.
// The core problem this replaces: severity alone ("Critical/Urgent/
// Moderate," borrowed from Condition Score's repair-urgency language)
// conflates "how bad is this" with "who has to do something about it." A
// recorded environmental covenant and three broken HOA-maintained exhaust
// fans both landed on "critical," with nothing distinguishing a permanent
// legal restriction from an active maintenance failure that isn't the
// buyer's problem to fix. Two changes:
//   1. Severity labels are per-category, not one global word bank — so
//      "critical" doesn't have to mean the same thing for a legal covenant
//      as for a malfunctioning fan.
//   2. Actor tags (Owner/HOA) are resolved from fields already extracted
//      (`affectedParties`, `obligations`, `summary`) via deterministic text
//      matching, not a new LLM judgment call — a wrong confident label is
//      worse than an absent one, so this only tags what the text actually
//      supports and falls back to a per-category default otherwise.

export type HOARiskActor = 'owner' | 'hoa';

export const HOA_RISK_ACTOR_LABEL: Record<HOARiskActor, string> = {
  owner: 'Owner',
  hoa: 'HOA',
};

type HOARiskSeverity = HOAEnvironmentalRiskFinding['severity'];

interface HOARiskCategoryDef {
  label: string;
  /** Actor shown when no finding-specific signal is found. `null` means
   *  property/title-level — binds everyone (owners, occupants, purchasers),
   *  not a specific actionable party, so no tag is shown by default. */
  defaultActor: HOARiskActor | null;
  severityLabels: Record<HOARiskSeverity, string>;
}

const STANDARD_SEVERITY_LABELS: Record<HOARiskSeverity, string> = {
  critical: 'Binding Restriction',
  high: 'Material Obligation',
  moderate: 'Notable Detail',
  informational: 'Informational',
};

export const HOA_RISK_CATEGORY_REGISTRY: Record<HOAEnvironmentalRiskCategory, HOARiskCategoryDef> =
  {
    'environmental-covenant': {
      label: 'Environmental Covenant',
      defaultActor: null,
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
    'environmental-monitoring': {
      label: 'Environmental Monitoring',
      defaultActor: 'hoa',
      // `critical` deliberately matches the standard "Binding Restriction" —
      // a required monitoring/mitigation system (e.g. "VMS required in every
      // building") is a recorded, legally binding requirement, not itself a
      // problem. "Active System Issue" only applies when hasActiveIssueSignal
      // actually detects failure language (resolveRiskLabel's override,
      // below) — baking it into this table directly was the bug the
      // regression test for finding #2 vs #13 caught: both are critical +
      // this category, but only #13 describes something actually broken.
      severityLabels: {
        critical: STANDARD_SEVERITY_LABELS.critical,
        high: 'Monitoring Finding',
        moderate: 'Monitoring Note',
        informational: 'Informational',
      },
    },
    'use-restriction': {
      label: 'Use Restriction',
      defaultActor: 'owner',
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
    'leasing-restriction': {
      label: 'Leasing Restriction',
      defaultActor: 'owner',
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
    'architectural-control': {
      label: 'Architectural Control',
      defaultActor: 'owner',
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
    'maintenance-obligation': {
      label: 'Maintenance Obligation',
      defaultActor: 'owner',
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
    'easement-access': {
      label: 'Easement / Access Right',
      defaultActor: null,
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
    'assessment-liability': {
      label: 'Financial / Assessment Liability',
      defaultActor: 'owner',
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
    'insurance-obligation': {
      label: 'Insurance Requirement',
      defaultActor: 'owner',
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
    other: {
      label: 'Other',
      defaultActor: null,
      severityLabels: STANDARD_SEVERITY_LABELS,
    },
  };

// Fires regardless of category — a finding describing an active equipment
// failure (fans off, near-zero flow, a missing part) reads wrong under a
// "restriction/obligation" label no matter what category it's filed under.
// Deliberately narrow (specific failure language, not general risk words)
// to avoid false-positiving on findings that just describe requirements.
const ACTIVE_ISSUE_RE =
  /\b(non-operational|not operating|could not be restarted|low flow|malfunction(ing)?|\bfailed\b|\bfailure\b|missing[^.]{0,20}\bport\b)\b/i;

function hasActiveIssueSignal(finding: HOAEnvironmentalRiskFinding): boolean {
  return ACTIVE_ISSUE_RE.test(`${finding.title} ${finding.summary}`);
}

/** The visible severity chip text — category-aware, with the active-issue
 *  override applied on top for critical/high findings describing a current
 *  equipment failure rather than a restriction or obligation. */
export function resolveRiskLabel(finding: HOAEnvironmentalRiskFinding): string {
  if (
    (finding.severity === 'critical' || finding.severity === 'high') &&
    hasActiveIssueSignal(finding)
  ) {
    return finding.severity === 'critical' ? 'Active System Issue' : 'Active System Concern';
  }
  const entry = HOA_RISK_CATEGORY_REGISTRY[finding.category] ?? HOA_RISK_CATEGORY_REGISTRY.other;
  return entry.severityLabels[finding.severity];
}

// Tooltip copy keyed by the resolved label text rather than raw severity —
// a label's meaning is fairly consistent regardless of which category
// produced it (e.g. "Binding Restriction" means the same thing whether it
// came from an environmental covenant or a use restriction).
const HOA_RISK_LABEL_TOOLTIP: Record<string, string> = {
  'Binding Restriction':
    'Recorded, legally active restriction or requirement — in effect now. Review with qualified counsel.',
  'Material Obligation': 'Affects ownership, use, or cost — should be understood before closing.',
  'Notable Detail': 'Worth understanding, not a legal red flag.',
  Informational: 'No restriction or obligation currently in effect.',
  'Active System Issue':
    "An HOA-maintained system currently isn't working as required — not something for you to personally fix.",
  'Active System Concern': 'A monitoring result worth attention on an HOA-maintained system.',
  'Monitoring Finding': 'A specific result from required environmental monitoring.',
  'Monitoring Note': 'Routine monitoring or reporting detail, not a current problem.',
};

export function resolveRiskTooltip(label: string): string | undefined {
  return HOA_RISK_LABEL_TOOLTIP[label];
}

// Color per resolved label, not per raw severity — the donut and any other
// summary visualization should break down by what these findings actually
// *are* (the label system above), not fall back to the Critical/Urgent/
// Moderate scale this taxonomy replaced. Intensity still roughly tracks the
// severity tier a label descends from (red > orange > yellow > neutral) so
// the chart reads consistently at a glance, but each label gets its own
// value rather than sharing one color with everything at the same severity.
const HOA_RISK_LABEL_COLOR: Record<string, string> = {
  'Binding Restriction': COLORS.critical,
  'Active System Issue': COLORS.urgent,
  'Active System Concern': COLORS.hoaPurple,
  'Material Obligation': COLORS.moderate,
  'Monitoring Finding': COLORS.accent1,
  'Notable Detail': COLORS.accent2,
  'Monitoring Note': COLORS.accent2,
  Informational: COLORS.monitor,
};

export function resolveRiskColor(label: string): string {
  return HOA_RISK_LABEL_COLOR[label] ?? COLORS.monitor;
}

// Rough severity-intensity ordering for consistent slice/list order — most
// significant first. Labels not listed (shouldn't happen given the table
// above, but the type is a plain string) sort last.
const HOA_RISK_LABEL_ORDER = [
  'Binding Restriction',
  'Active System Issue',
  'Material Obligation',
  'Active System Concern',
  'Monitoring Finding',
  'Notable Detail',
  'Monitoring Note',
  'Informational',
];

export function compareRiskLabelOrder(a: string, b: string): number {
  const ai = HOA_RISK_LABEL_ORDER.indexOf(a);
  const bi = HOA_RISK_LABEL_ORDER.indexOf(b);
  return (
    (ai === -1 ? HOA_RISK_LABEL_ORDER.length : ai) - (bi === -1 ? HOA_RISK_LABEL_ORDER.length : bi)
  );
}

// Owner/HOA signal patterns — matched against title, summary, obligations,
// and affectedParties (the field most likely to state a party explicitly,
// e.g. "HOA (responsible for repairs)"). Deliberately conservative: matches
// only explicit subject+verb obligation language, not any mention of the
// word "owner" or "HOA" — a finding that merely lists a party as bound
// (e.g. a covenant's affectedParties including "HOA (Association)" among
// five other parties) should fall through to the category default rather
// than claim a specific actor the text doesn't actually assign a duty to.
const OWNER_SIGNAL_RE =
  /\bowner(s)?\s+(shall|must|is|are|may not|is required|will|remains?)\b|owner-required|personal liability|each owner/i;
const HOA_SIGNAL_RE = new RegExp(
  '\\b(the\\s+)?(hoa|association)\\s+' +
    '(shall|must|is responsible|is the responsible party|will|operates|maintains|bears|is required to)\\b' +
    '|association\\s+shall' +
    '|\\(responsible for repairs\\)',
  'i',
);
// Excludes "the Association may cure owner deficiencies"-style enforcement
// backstops — that's the HOA's fallback remedy if the owner doesn't comply,
// not a parallel duty worth an equal-weight HOA tag.
const HOA_ENFORCEMENT_EXCLUDE_RE = /(hoa|association)[^.]{0,20}\bcure\b/i;

function actorMatchText(finding: HOAEnvironmentalRiskFinding): string {
  return [finding.title, finding.summary, ...finding.obligations, ...finding.affectedParties].join(
    ' ',
  );
}

/** Known gap: negation constructions ("neither Owner nor the Association
 *  shall...") aren't caught by the positive subject+verb patterns above, so
 *  a genuinely joint prohibition can under-report to a single actor (or the
 *  category default). Accepted tradeoff over a regex broad enough to
 *  false-positive on unrelated mentions — revisit once more real HOA
 *  packets are processed and this can be tuned against a larger sample. */
export function resolveRiskActors(finding: HOAEnvironmentalRiskFinding): HOARiskActor[] {
  const text = actorMatchText(finding);
  const actors: HOARiskActor[] = [];
  if (OWNER_SIGNAL_RE.test(text)) actors.push('owner');
  if (HOA_SIGNAL_RE.test(text) && !HOA_ENFORCEMENT_EXCLUDE_RE.test(text)) actors.push('hoa');
  if (actors.length > 0) return actors;

  const entry = HOA_RISK_CATEGORY_REGISTRY[finding.category] ?? HOA_RISK_CATEGORY_REGISTRY.other;
  return entry.defaultActor ? [entry.defaultActor] : [];
}

// ─── Document grouping ─────────────────────────────────────────────────────
// Findings from the same underlying legal instrument or report read as a
// coherent packet instead of 17 flattened, independent rows — and this also
// resolves the near-duplicate-findings problem for free (e.g. the CC&Rs'
// VMS requirement and the Annual Report's VIMS requirement visibly sit
// under two different source documents instead of reading as redundant).
// Grouped by recorded document number when present (finer-grained — the
// Environmental Covenant and the main CC&Rs are two different recorded
// documents that happen to share one uploaded PDF), falling back to
// sourceDocumentType otherwise (covers monitoring/inspection reports, which
// cite a GeoTracker/permit ID instead of a recorded doc number).

export interface HOARiskDocumentGroup {
  key: string;
  label: string;
  risks: HOAEnvironmentalRiskFinding[];
}

const SOURCE_DOCUMENT_TYPE_LABEL: Record<string, string> = {
  HOA_CCRS: 'CC&Rs',
  HOA_ENVIRONMENTAL_REPORT: 'Environmental Monitoring Report',
};

function extractRecordedDocNumber(ref: string | null | undefined): string | null {
  return ref?.match(/Doc #?(\d+)/i)?.[1] ?? null;
}

function extractRecordedYear(ref: string | null | undefined): string | null {
  return ref?.match(/\b(19|20)\d{2}\b/)?.[0] ?? null;
}

export function groupRisksByDocument(risks: HOAEnvironmentalRiskFinding[]): HOARiskDocumentGroup[] {
  const groups: HOARiskDocumentGroup[] = [];
  const groupByKey = new Map<string, HOARiskDocumentGroup>();

  for (const risk of risks) {
    const docNumber = extractRecordedDocNumber(risk.recordedReference);
    const key = docNumber ? `doc-${docNumber}` : risk.sourceDocumentType ?? 'unknown';

    const existing = groupByKey.get(key);
    if (existing) {
      existing.risks.push(risk);
      continue;
    }
    // Placeholder label — resolved for real once every finding in the
    // group is known (see below). A recorded doc number alone ("CC&Rs —
    // Recorded Doc #23879598") doesn't tell a buyer why two groups exist
    // when they only uploaded one PDF; a group built around an actual
    // environmental covenant needs to say so.
    const group: HOARiskDocumentGroup = { key, label: '', risks: [risk] };
    groupByKey.set(key, group);
    groups.push(group);
  }

  for (const group of groups) {
    const sample = group.risks[0];
    const docNumber = extractRecordedDocNumber(sample.recordedReference);
    const typeLabel =
      SOURCE_DOCUMENT_TYPE_LABEL[sample.sourceDocumentType ?? ''] ?? 'Source Document';

    if (docNumber && group.risks.some((r) => r.category === 'environmental-covenant')) {
      const year = extractRecordedYear(sample.recordedReference);
      group.label = year
        ? `Recorded Environmental Covenant (${year})`
        : 'Recorded Environmental Covenant';
    } else {
      group.label = typeLabel;
    }
  }

  return groups;
}
